import React, { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, Pencil, RefreshCw, X, Save, ServerCog, Search, Mail } from "lucide-react";
import {
  adminGetUsers,
  adminCreateUser,
  adminUpdateUser,
  adminDeleteUser,
  adminGetPlatformStackUsers,
  adminSyncUsersFromPlatform,
  adminSendPasswordSetup,
} from "../../lib/api";

const empty = { name: "", email: "", password: "", role: "user", status: "active" };

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] font-semibold text-[#11151C]/70 uppercase tracking-wider">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function Modal({ open, onClose, title, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-[#11151C]/60 flex items-center justify-center p-4" data-testid="user-modal-backdrop">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#11151C]/10">
          <h3 className="lm-font font-extrabold text-[#11151C]">{title}</h3>
          <button onClick={onClose} className="text-[#11151C]/50 hover:text-[#11151C]" data-testid="user-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

export default function UsersPanel({ onToast }) {
  const [users, setUsers] = useState([]);
  const [stackUsers, setStackUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null); // user obj or null
  const [form, setForm] = useState(empty);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all | admin | user | platform

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminGetUsers();
      setUsers(Array.isArray(data) ? data : []);
    } catch (e) {
      onToast?.("Failed to load users", "error");
    }
    setLoading(false);
  }, [onToast]);

  const loadStack = useCallback(async () => {
    try {
      const data = await adminGetPlatformStackUsers();
      setStackUsers(data?.users || []);
    } catch (e) {
      // silent fail; sync button still works
    }
  }, []);

  useEffect(() => {
    load();
    loadStack();
  }, [load, loadStack]);

  const handleCreate = async () => {
    if (!form.email || !form.password) {
      onToast?.("Email and password are required", "error");
      return;
    }
    try {
      await adminCreateUser(form);
      setShowCreate(false);
      setForm(empty);
      onToast?.("User created");
      await load();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Create failed", "error");
    }
  };

  const handleUpdate = async () => {
    if (!editing) return;
    const payload = { ...form };
    if (!payload.password) delete payload.password;
    try {
      await adminUpdateUser(editing.id, payload);
      setEditing(null);
      setForm(empty);
      onToast?.("User updated");
      await load();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Update failed", "error");
    }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Delete user ${u.email}? This cannot be undone.`)) return;
    try {
      await adminDeleteUser(u.id);
      onToast?.("User deleted");
      await load();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Delete failed", "error");
    }
  };

  const handleSendPasswordSetup = async (u) => {
    if (!u?.email || u.email.endsWith(".local")) {
      onToast?.("This user has no real email address. Edit the user and set one first.", "error");
      return;
    }
    if (!window.confirm(`Email a password-setup link to ${u.email}?`)) return;
    try {
      const res = await adminSendPasswordSetup(u.id);
      onToast?.(`Setup email sent (expires in ${res.expires_in_minutes} min)`);
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Send failed", "error");
    }
  };

  const handleSyncPlatform = async () => {
    setLoading(true);
    try {
      const res = await adminSyncUsersFromPlatform();
      onToast?.(`Synced from platform — created ${res.created}, skipped ${res.skipped}`);
      await load();
      await loadStack();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Platform sync failed", "error");
    }
    setLoading(false);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({
      name: u.name || "",
      email: u.email || "",
      password: "",
      role: u.role || "user",
      status: u.status || "active",
    });
  };

  const filtered = users.filter((u) => {
    if (filter === "admin" && u.role !== "admin") return false;
    if (filter === "user" && u.role !== "user") return false;
    if (filter === "platform" && u.provider !== "platform") return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        (u.email || "").toLowerCase().includes(q) ||
        (u.name || "").toLowerCase().includes(q) ||
        (u.stack_username || "").toLowerCase().includes(q)
      );
    }
    return true;
  });

  return (
    <div data-testid="users-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h2 className="lm-font font-extrabold text-[#11151C] text-2xl">Website Users</h2>
          <p className="text-[#11151C]/60 text-sm mt-1">
            Manage admin & customer accounts. Sync control panel users from your hosting platform to keep everything in one place.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={handleSyncPlatform}
            disabled={loading}
            data-testid="users-sync-platform"
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm font-semibold border border-[#11151C]/15 bg-white hover:bg-[#FFE6D6]/40 disabled:opacity-50"
          >
            <ServerCog className="w-4 h-4" /> Sync Platform Users
          </button>
          <button
            onClick={load}
            data-testid="users-refresh"
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm font-semibold border border-[#11151C]/15 bg-white hover:bg-[#FFE6D6]/40"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          <button
            onClick={() => { setForm(empty); setShowCreate(true); }}
            data-testid="users-add-button"
            className="lm-btn-primary inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold"
          >
            <Plus className="w-4 h-4" /> Add User
          </button>
        </div>
      </div>

      <div className="grid sm:grid-cols-4 gap-3 mb-4">
        <div className="border border-[#11151C]/10 rounded-xl p-3">
          <div className="text-[11px] uppercase font-bold text-[#11151C]/55 tracking-wider">Total Users</div>
          <div className="lm-font font-extrabold text-2xl text-[#11151C]">{users.length}</div>
        </div>
        <div className="border border-[#11151C]/10 rounded-xl p-3">
          <div className="text-[11px] uppercase font-bold text-[#11151C]/55 tracking-wider">Admins</div>
          <div className="lm-font font-extrabold text-2xl text-[#11151C]">
            {users.filter((u) => u.role === "admin").length}
          </div>
        </div>
        <div className="border border-[#11151C]/10 rounded-xl p-3">
          <div className="text-[11px] uppercase font-bold text-[#11151C]/55 tracking-wider">From Platform</div>
          <div className="lm-font font-extrabold text-2xl text-[#11151C]">
            {users.filter((u) => u.provider === "platform").length}
          </div>
        </div>
        <div className="border border-[#11151C]/10 rounded-xl p-3">
          <div className="text-[11px] uppercase font-bold text-[#11151C]/55 tracking-wider">Control Panel (live)</div>
          <div className="lm-font font-extrabold text-2xl text-[#11151C]">{stackUsers.length}</div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-[#11151C]/40 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, or control panel user…"
            data-testid="users-search"
            className="w-full pl-9 pr-3 py-2 border border-[#11151C]/15 rounded-xl text-sm focus:outline-none focus:border-[#FF6B3D]/50 focus:ring-2 focus:ring-[#FF6B3D]/20"
          />
        </div>
        <div className="flex gap-1 bg-[#FAF8F4] rounded-xl p-1 border border-[#11151C]/10">
          {["all", "admin", "user", "platform"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`users-filter-${f}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${
                filter === f ? "bg-[#11151C] text-white" : "text-[#11151C]/60 hover:text-[#11151C]"
              }`}
            >
              {f.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      <div className="border border-[#11151C]/10 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#FFFBF6]">
            <tr>
              <th className="text-left p-3">Name</th>
              <th className="text-left p-3">Email</th>
              <th className="text-left p-3">Role</th>
              <th className="text-left p-3">Provider</th>
              <th className="text-left p-3">Control Panel</th>
              <th className="text-left p-3">Support PIN</th>
              <th className="text-left p-3">Joined</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={8} className="p-8 text-center text-[#11151C]/50">
                  {loading ? "Loading…" : "No users match."}
                </td>
              </tr>
            )}
            {filtered.map((u) => (
              <tr key={u.id} className="border-t border-[#11151C]/5 hover:bg-[#FFFBF6]/40" data-testid={`user-row-${u.id}`}>
                <td className="p-3 font-semibold">{u.name || "—"}</td>
                <td className="p-3 break-all">{u.email}</td>
                <td className="p-3">
                  <span className={`text-[11px] font-bold px-2 py-1 rounded ${
                    u.role === "admin" ? "bg-[#FFE6D6] text-[#B23B12]" : "bg-gray-100 text-gray-700"
                  }`}>
                    {u.role}
                  </span>
                </td>
                <td className="p-3 text-xs text-[#11151C]/60">{u.provider || "local"}</td>
                <td className="p-3 text-xs text-[#11151C]/60">{u.stack_username || u.stack_user_ref || "—"}</td>
                <td className="p-3"><code className="text-[11px] font-mono bg-[#FFFBF6] border border-[#FF6B3D]/20 text-[#B23B12] px-2 py-0.5 rounded">{u.support_pin || "—"}</code></td>
                <td className="p-3 text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                <td className="p-3">
                  <div className="flex justify-end gap-1">
                    <button
                      onClick={() => handleSendPasswordSetup(u)}
                      data-testid={`user-send-setup-${u.id}`}
                      className="p-1.5 text-[#11151C]/70 hover:text-[#FF6B3D] hover:bg-[#FFE6D6]/40 rounded-lg"
                      title="Send password-setup email"
                    >
                      <Mail className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => openEdit(u)}
                      data-testid={`user-edit-${u.id}`}
                      className="p-1.5 text-[#11151C]/70 hover:text-[#FF6B3D] hover:bg-[#FFE6D6]/40 rounded-lg"
                      title="Edit"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(u)}
                      data-testid={`user-delete-${u.id}`}
                      className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create modal */}
      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Add User">
        <div className="space-y-3">
          <Field label="Name">
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="user-form-name"
              className="w-full border border-[#11151C]/15 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#FF6B3D]/50 focus:ring-2 focus:ring-[#FF6B3D]/20"
            />
          </Field>
          <Field label="Email *">
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="user-form-email"
              className="w-full border border-[#11151C]/15 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#FF6B3D]/50 focus:ring-2 focus:ring-[#FF6B3D]/20"
            />
          </Field>
          <Field label="Password *">
            <input
              type="text"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              data-testid="user-form-password"
              className="w-full border border-[#11151C]/15 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#FF6B3D]/50 focus:ring-2 focus:ring-[#FF6B3D]/20"
            />
          </Field>
          <Field label="Role">
            <select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              data-testid="user-form-role"
              className="w-full border border-[#11151C]/15 rounded-xl px-3 py-2 text-sm bg-white"
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </Field>
          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={() => setShowCreate(false)}
              className="px-4 py-2 rounded-xl text-sm font-semibold border border-[#11151C]/15"
            >
              Cancel
            </button>
            <button
              onClick={handleCreate}
              data-testid="user-form-create-submit"
              className="lm-btn-primary inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold"
            >
              <Save className="w-4 h-4" /> Create
            </button>
          </div>
        </div>
      </Modal>

      {/* Edit modal */}
      <Modal open={!!editing} onClose={() => setEditing(null)} title={`Edit · ${editing?.email || ""}`}>
        <div className="space-y-3">
          <Field label="Name">
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="user-edit-name"
              className="w-full border border-[#11151C]/15 rounded-xl px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Email">
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="user-edit-email"
              className="w-full border border-[#11151C]/15 rounded-xl px-3 py-2 text-sm"
            />
          </Field>
          <Field label="New password (leave blank to keep)">
            <input
              type="text"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              data-testid="user-edit-password"
              className="w-full border border-[#11151C]/15 rounded-xl px-3 py-2 text-sm"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Role">
              <select
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                data-testid="user-edit-role"
                className="w-full border border-[#11151C]/15 rounded-xl px-3 py-2 text-sm bg-white"
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </Field>
            <Field label="Status">
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                data-testid="user-edit-status"
                className="w-full border border-[#11151C]/15 rounded-xl px-3 py-2 text-sm bg-white"
              >
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
              </select>
            </Field>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={() => setEditing(null)}
              className="px-4 py-2 rounded-xl text-sm font-semibold border border-[#11151C]/15"
            >
              Cancel
            </button>
            <button
              onClick={handleUpdate}
              data-testid="user-edit-submit"
              className="lm-btn-primary inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold"
            >
              <Save className="w-4 h-4" /> Save Changes
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
